import { useEffect } from 'react';
import { App } from '@capacitor/app';
import { isNativeApp, getCurrentPlatform } from '../utils/platformUtils';

interface OAuthCallbackHandlerProps {
  onAuthComplete?: (success: boolean) => void;
}

export function OAuthCallbackHandler({ onAuthComplete }: OAuthCallbackHandlerProps) {
  useEffect(() => {
    try {
      console.log('[OAuthCallbackHandler] ========== COMPONENT MOUNTING ==========');
      console.log('[OAuthCallbackHandler] Component mounted, isNativeApp:', isNativeApp());
      console.log('[OAuthCallbackHandler] Platform:', getCurrentPlatform());
      console.log('[OAuthCallbackHandler] Current window.location:', window.location.href);
      console.log('[OAuthCallbackHandler] Current window.location.pathname:', window.location.pathname);
      console.log('[OAuthCallbackHandler] Current window.location.search:', window.location.search);
      console.log('[OAuthCallbackHandler] Current window.location.hostname:', window.location.hostname);
      
      if (!isNativeApp()) {
        console.log('[OAuthCallbackHandler] Not a native app, skipping setup');
        return;
      }
      
      console.log('[OAuthCallbackHandler] Native app detected, setting up listeners...');
    } catch (error) {
      console.error('[OAuthCallbackHandler] Error in useEffect setup:', error);
      return;
    }

    const handleOAuthCallback = async (url: string) => {
      console.log('[OAuthCallbackHandler] Processing OAuth callback from URL:', url);
      
      // Parse URL - handle both standard URLs and custom scheme URLs
      let urlObj: URL;
      try {
        urlObj = new URL(url);
      } catch (e) {
        // For custom scheme URLs, try to parse manually
        if (url.startsWith('healthscores://')) {
          const match = url.match(/^healthscores:\/\/oauth\/callback\?(.*)$/);
          if (match) {
            const params = new URLSearchParams(match[1].split('#')[0]); // Remove hash if present
            urlObj = new URL('http://dummy?' + params.toString());
          } else {
            console.error('[OAuthCallbackHandler] Failed to parse custom scheme URL:', url);
            onAuthComplete?.(false);
            return;
          }
        } else {
          console.error('[OAuthCallbackHandler] Failed to parse URL:', url, e);
          onAuthComplete?.(false);
          return;
        }
      }
      
      const code = urlObj.searchParams.get('code');
      const error = urlObj.searchParams.get('error');
      const state = urlObj.searchParams.get('state');
      const token = urlObj.searchParams.get('token');
      
      // Check if Byteowls plugin is handling this OAuth flow
      const oauthInProgress = localStorage.getItem('oauth_in_progress');
      if (oauthInProgress === 'byteowls_google') {
        console.log('[OAuthCallbackHandler] Byteowls plugin is handling OAuth, capturing code/error as fallback...');
        console.log('[OAuthCallbackHandler] Extracted params:', { hasCode: !!code, hasError: !!error, hasState: !!state, hasToken: !!token });
        
        if (error) {
          console.error('[OAuthCallbackHandler] OAuth error captured:', error);
          localStorage.setItem('google_drive_oauth_error', error);
          localStorage.removeItem('oauth_in_progress');
          return;
        }
        
        if (token) {
          // Token was passed directly (from oauth-callback.html)
          try {
            const tokenData = JSON.parse(decodeURIComponent(token));
            if (tokenData.access_token) {
              console.log('[OAuthCallbackHandler] Token received directly, storing...');
              localStorage.setItem('google_drive_oauth_token', JSON.stringify({
                access_token: tokenData.access_token,
                expires_in: tokenData.expires_in || 3600
              }));
              localStorage.removeItem('oauth_in_progress');
              onAuthComplete?.(true);
              return;
            }
          } catch (e) {
            console.error('[OAuthCallbackHandler] Failed to parse token data:', e);
          }
        }
        
        if (code) {
          console.log('[OAuthCallbackHandler] Code captured, storing for fallback use. Code length:', code.length);
          localStorage.setItem('google_drive_oauth_code', JSON.stringify({ code }));
          console.log('[OAuthCallbackHandler] Code stored in localStorage');
          return;
        } else {
          console.warn('[OAuthCallbackHandler] No code, error, or token found in URL:', url);
        }
        return;
      }
      
      // Handle error case for non-Byteowls flow
      if (error) {
        console.error('[OAuthCallbackHandler] OAuth error:', error);
        // Store error for potential error handling
        if (oauthInProgress) {
          localStorage.setItem('google_drive_oauth_error', error);
          localStorage.removeItem('oauth_in_progress');
        }
        onAuthComplete?.(false);
        return;
      }
      
      // Handle token case (from oauth-callback.html success)
      if (token) {
        try {
          const tokenData = JSON.parse(decodeURIComponent(token));
          if (tokenData.access_token) {
            console.log('[OAuthCallbackHandler] Token received directly, storing...');
            localStorage.setItem('google_drive_oauth_token', JSON.stringify({
              access_token: tokenData.access_token,
              expires_in: tokenData.expires_in || 3600
            }));
            onAuthComplete?.(true);
            return;
          }
        } catch (e) {
          console.error('[OAuthCallbackHandler] Failed to parse token data:', e);
          onAuthComplete?.(false);
          return;
        }
      }
      
      try {
        if (!code || !state) {
          console.warn('[OAuthCallbackHandler] Missing code or state in URL', { code: !!code, state: !!state });
          onAuthComplete?.(false);
          return;
        }

        console.log('[OAuthCallbackHandler] Exchanging code for token via Universal Link');
        
        // Get auth headers
        const bearerToken = localStorage.getItem('bearer_token');
        if (!bearerToken) {
          console.error('[OAuthCallbackHandler] No bearer token found - user must be logged in first');
          onAuthComplete?.(false);
          return;
        }

        // Exchange code for token via backend
        const response = await fetch('/callback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${bearerToken}`
          },
          body: JSON.stringify({ code, state })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.access_token) {
            // Store token in localStorage for mobileGoogleDriveAuth to pick up via polling
            localStorage.setItem('google_drive_oauth_token', JSON.stringify({
              access_token: result.access_token,
              expires_in: result.expires_in
            }));
            console.log('[OAuthCallbackHandler] Token stored successfully');
            onAuthComplete?.(true);
          } else {
            console.error('[OAuthCallbackHandler] No access token in response');
            onAuthComplete?.(false);
          }
        } else {
          const errorText = await response.text().catch(() => 'Unknown error');
          console.error('[OAuthCallbackHandler] Token exchange failed:', response.status, errorText);
          onAuthComplete?.(false);
        }
      } catch (error) {
        console.error('[OAuthCallbackHandler] Error handling OAuth callback:', error);
        onAuthComplete?.(false);
      }
    };

    // Check current URL on mount in case Universal Link already opened
    const checkCurrentUrl = () => {
      const currentUrl = window.location.href;
      const pathname = window.location.pathname;
      const search = window.location.search;
      
      // Check if we're on a callback route (either /callback or /login/callback)
      const isCallbackRoute = pathname === '/callback' || pathname === '/login/callback';
      const hasCallbackParams = search.includes('code=') && search.includes('state=');
      const hasCodeOnly = search.includes('code='); // For Byteowls flow which might not have state
      const hasErrorParam = search.includes('error=');
      const hasTokenParam = search.includes('token=');
      const isCallbackDomain = currentUrl.includes('healthscores.ai/callback') || currentUrl.includes('dev.healthscores.ai/callback');
      
      // Check if Byteowls is in progress - capture code/error/token even without state
      const oauthInProgress = localStorage.getItem('oauth_in_progress');
      const isByteowlsCallback = oauthInProgress === 'byteowls_google' && (hasCodeOnly || hasErrorParam || hasTokenParam) && isCallbackRoute;
      
      // Check if this is a callback URL (with code, error, or token parameters)
      const isCallback = (isCallbackRoute && (hasCallbackParams || hasCodeOnly || hasErrorParam || hasTokenParam)) || 
                         isCallbackDomain || 
                         isByteowlsCallback;
      
      if (isCallback) {
        console.log('[OAuthCallbackHandler] Found callback URL in window.location, processing...', {
          href: currentUrl,
          pathname,
          search,
          isCallbackRoute,
          hasCallbackParams,
          hasCodeOnly,
          hasErrorParam,
          hasTokenParam,
          isCallbackDomain,
          isByteowlsCallback
        });
        handleOAuthCallback(currentUrl);
      }
      // Only log if we're actually on a callback route but missing params (for debugging)
      else if (isCallbackRoute) {
        console.log('[OAuthCallbackHandler] On callback route but no callback parameters found');
      }
    };

    // Check immediately and also after a short delay (in case React Router hasn't navigated yet)
    checkCurrentUrl();
    setTimeout(checkCurrentUrl, 100);
    setTimeout(checkCurrentUrl, 500);

    const handleAppStateChange = async (state: any) => {
      console.log('[OAuthCallbackHandler] App state changed:', state);
      if (state.isActive) {
        // Check URL when app becomes active (in case Universal Link opened in webview)
        const currentUrl = window.location.href;
        const pathname = window.location.pathname;
        const search = window.location.search;
        
        console.log('[OAuthCallbackHandler] App became active, checking URL:', {
          href: currentUrl,
          pathname,
          search
        });
        
        const isCallbackRoute = pathname === '/callback' || pathname === '/login/callback';
        const hasCallbackParams = search.includes('code=') && search.includes('state=');
        const isCallbackDomain = currentUrl && (currentUrl.includes('healthscores.ai/callback') || currentUrl.includes('dev.healthscores.ai/callback'));
        
        if ((isCallbackRoute && hasCallbackParams) || isCallbackDomain) {
          console.log('[OAuthCallbackHandler] Found callback URL when app became active, processing...');
          handleOAuthCallback(currentUrl);
          return;
        }

        // App became active, check if we have a pending auth (requires app login token)
        const bearerToken = localStorage.getItem('bearer_token');
        if (!bearerToken) return;
        try {
          const response = await fetch('/api/google-drive/token', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${bearerToken}`,
            }
          });

          if (response.ok) {
            const result = await response.json();
            if (result.access_token) {
              onAuthComplete?.(true);
            }
          }
        } catch (error) {
          console.error('Error checking auth status:', error);
        }
      }
    };

    const handleUrlOpen = async (event: any) => {
      const url = event?.url;
      
      if (!url) {
        return;
      }
      
      // Early exit for file:// URLs that aren't OAuth callbacks
      // File URLs for documents/files should be ignored unless they're OAuth-related
      if (url.startsWith('file://')) {
        // Only process file:// URLs if they're explicitly OAuth callbacks (shouldn't happen, but check anyway)
        const isOAuthFile = url.includes('/callback') || url.includes('oauth/callback') || url.includes('code=') || url.includes('error=') || url.includes('token=');
        if (!isOAuthFile) {
          // This is a regular file URL (like a PDF or document) - ignore it
          return;
        }
      }
      
      console.log('[OAuthCallbackHandler] appUrlOpen event received:', url, 'Full event:', event);
      
      // Check if this is a callback URL (Universal Link or any callback with code/error/token parameter)
      const isUniversalLink = url.includes('healthscores.ai/callback') || url.includes('dev.healthscores.ai/callback');
      const isCallbackWithCode = url.includes('/callback') && url.includes('code=');
      const isCallbackWithError = url.includes('/callback') && url.includes('error=');
      const isCallbackWithToken = url.includes('/callback') && url.includes('token=');
      const isCustomSchemeCallback = url.startsWith('healthscores://oauth/callback');
      
      console.log('[OAuthCallbackHandler] URL detection:', {
        isUniversalLink,
        isCallbackWithCode,
        isCallbackWithError,
        isCallbackWithToken,
        isCustomSchemeCallback,
        url
      });
      
      // If Byteowls is in progress, capture code/error/token from any callback URL
      const oauthInProgress = localStorage.getItem('oauth_in_progress');
      console.log('[OAuthCallbackHandler] oauth_in_progress:', oauthInProgress);
      
      if (oauthInProgress === 'byteowls_google' && (isCallbackWithCode || isCallbackWithError || isCallbackWithToken || isCustomSchemeCallback)) {
        console.log('[OAuthCallbackHandler] Byteowls OAuth in progress, capturing callback from URL');
        handleOAuthCallback(url);
        return;
      }
      
      // Handle Universal Links (https://healthscores.ai/callback)
      // iOS intercepts the HTTPS URL and opens it directly in the app
      if (isUniversalLink) {
        console.log('[OAuthCallbackHandler] Detected Universal Link in appUrlOpen event');
        handleOAuthCallback(url);
      } else if (isCallbackWithCode || isCallbackWithError || isCallbackWithToken || isCustomSchemeCallback) {
        console.log('[OAuthCallbackHandler] Detected callback URL with code/error/token parameter or custom scheme');
        handleOAuthCallback(url);
      } else {
        console.log('[OAuthCallbackHandler] appUrlOpen event is not a callback URL:', url);
      }
    };

    // Listen for app state changes and URL open events (deep links)
    let appStateListener: any = null;
    let urlOpenListener: any = null;

    console.log('[OAuthCallbackHandler] Registering appStateChange listener...');
    App.addListener('appStateChange', handleAppStateChange).then(listener => {
      appStateListener = listener;
      console.log('[OAuthCallbackHandler] appStateChange listener registered successfully');
    }).catch(error => {
      console.error('[OAuthCallbackHandler] Failed to register appStateChange listener:', error);
    });

    console.log('[OAuthCallbackHandler] Registering appUrlOpen listener...');
    App.addListener('appUrlOpen', handleUrlOpen).then(listener => {
      urlOpenListener = listener;
      console.log('[OAuthCallbackHandler] appUrlOpen listener registered successfully');
    }).catch(error => {
      console.error('[OAuthCallbackHandler] Failed to register appUrlOpen listener:', error);
    });

    return () => {
      // Remove listeners if they were created
      if (appStateListener) {
        appStateListener.remove();
      }
      if (urlOpenListener) {
        urlOpenListener.remove();
      }
    };
  }, [onAuthComplete]);

  return null; // This component doesn't render anything
}
